import useApolloSetup from '../../hooks/useApolloSetup'
import {addHocs} from './addhocs'
import Box from '@mui/material/Box'
import PropTypes from 'prop-types'
import CssBaseline from '@mui/material/CssBaseline'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ThemeProvider
} from '@mui/material'
import theme from '../../themes'
import {Routing} from '../Routing'
import {useDispatch, useSelector} from 'react-redux'
import {useIsAuthenticated, useMsal} from '@azure/msal-react'
import {useEffect, useRef} from 'react'
import {setUserInfo} from '../../store/userProfile/userProfileSlice'
import {StyledBox} from './Styles'
import IdleTimeoutNotification from '../IdleTimeout/IdleTimeoutNotification'
import TimeoutConfig from '../../configs/timeoutConfig.json'
import IdleTimer from '../../helper/IdleTimer'
import {setIsTimeout} from '../../store/idleTimeout/idleTimeoutSlice'
import {useApolloClient} from '@apollo/client'
import HeaderIndex from '../Header'

const Unauthorized = ({handleClose}) => (
  <Dialog open onClose={handleClose} aria-labelledby="dialog-title">
    <DialogTitle id="dialog-title">Access Denied</DialogTitle>
    <DialogContent>
      <DialogContentText>You are not authorized to view this resource.</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={handleClose} color="primary">
        Ok
      </Button>
    </DialogActions>
  </Dialog>
)

const App = () => {
  const dispatch = useDispatch()
  const {accounts, instance} = useMsal()
  const client = useApolloClient()
  const isAuthenticated = useIsAuthenticated()

  const timerRef = useRef()

  const getIdleTimeout = useSelector((state) => state.idleTimeout)
  const {isTimeout} = getIdleTimeout

  useEffect(() => {
    if (isAuthenticated) {
      const [currentAccount] = accounts
      const {roles} = currentAccount?.idTokenClaims || {}
      dispatch(
        setUserInfo({
          name: currentAccount?.name,
          email: currentAccount?.username,
          roles: roles || ['Invalid']
        })
      )
    }
  }, [accounts, isAuthenticated, dispatch])

  useEffect(() => {
    timerRef.current = new IdleTimer({
      timeout: TimeoutConfig.APP_SESSION_TIMEOUT_SECONDS, // expire after 20 mins
      onTimeout: () => {
        dispatch(setIsTimeout(true))
      }
    })
    if (isTimeout) {
      return () => {
        handleLogout()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogout = () => {
    if (timerRef?.current != null) timerRef.current.cleanUp()
    client?.clearStore()
    instance?.logoutRedirect()
  }

  const setTimeOutInactive = () => {
    dispatch(setIsTimeout(false))
  }

  if (!isAuthenticated) {
    return (
      <Unauthorized
        handleClose={() => {
          handleLogout()
        }}
      />
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className="App" sx={{display: 'flex'}}>
        <CssBaseline />
        <StyledBox component="main">
          <HeaderIndex />
          <Routing />
          <IdleTimeoutNotification isOpen={isTimeout} setTimeOutInactive={setTimeOutInactive} />
        </StyledBox>
      </Box>
    </ThemeProvider>
  )
}

Unauthorized.propTypes = {
  handleClose: PropTypes.func
}

export default addHocs(App, [useApolloSetup])
